import {
  BILLING_PRICE_LABELS,
  BILLING_WEB_PRICE_LABELS,
  checkBillingStatus,
  coverageAvailable,
  openElsewhere,
  type BillingProductKey,
  type BillingStatus,
  type CoverageRecord,
  type InspectionCoverage,
  type InspectionKind,
  type InspectionPermission,
  type PlanKind,
  type RedeemOutcome,
  type ReleaseOutcome
} from '../../supabase/functions/_shared/billingPolicy';
import { deviceId, thisDevice } from './accountStore';
import { currentAccessToken } from '../backend/aiClient';
import { currentBillingIdentity, getBillingTransport, type BillingFailureCode } from '../backend/billingClient';
import { getWebBillingTransport } from '../backend/webBillingClient';
import { rememberWebCheckout } from './webPurchaseReturn';
import { getPurchasesAdapter } from '../native/purchasesAdapter';
import type { PurchaseFailureCode, StoreEntitlement } from '../native/purchasesContract';
import { assertV2Namespace, V2_BILLING_CACHE_KEY } from '../persistence/persistenceContract';
import { createStore } from './createStore';
import { sessionStore } from './sessionStore';
import type { AiV2Quota } from '../../supabase/functions/_shared/aiV2Protocol';

// Purchases and AI allowance — the ONE place the app asks "may this inspector create a formal
// inspection, and how much AI allowance is left".
//
// AUTHORITY: OWNER-DECISIONS-20260911.md (project root) D-3 / D-4 / D-5 and the line defaults
// X-2 … X-7. The rules themselves live in supabase/functions/_shared/billingPolicy.ts; this store
// only holds state and talks to the two outside worlds:
//
//   the store   Apple's sheet through RevenueCat (src/native/purchasesAdapter.ts) — it is what
//               SELLS, and its verdict on what was bought is the only one that counts;
//   the server  supabase/functions/billing-v2 — it is what REMEMBERS: how much allowance is left
//               and which inspection already consumed a purchase (X-7). The phone is never the
//               source of truth; it shows the server's number and caches it so field work
//               continues offline.
//
// OFFLINE (the field case this product exists for): an inspector whose plan is active, or who is
// holding an unused single purchase, can create a formal inspection with no network; the
// consumption is recorded locally and reconciled the next time the server is reachable. With no
// plan and no credit and no network, a FORMAL inspection cannot be created — the app says exactly
// that (a Demo is always free, X-4).
export type BillingBusy = BillingProductKey | 'restore' | 'offerCode' | 'refresh' | 'redeem';

export interface PendingConsumption {
  inspectionId: string;
  coverage: InspectionCoverage;
  at: number;
  // The server refused to fund it (the credit was already spent elsewhere while this device was
  // offline). Kept so the state is visible instead of silently wrong.
  rejected?: boolean;
}

export type BillingServerState = 'unknown' | 'ok' | 'offline' | 'unavailable' | 'notConfigured' | 'unauthenticated';

// What this device still has to tell the server about one inspection (billingPolicy.ts V-1 … V-3), kept per
// account so it survives a closed app and is sent the next time the server is reachable:
//   release  an unfinished formal inspection was deleted here
//   close    its final report was generated here
//   hold     a file carried it to this device
export interface BillingNotice {
  kind: 'release' | 'close' | 'hold';
  inspectionId: string;
  at: number;
}

// What deleting an unfinished inspection did to what paid for it.
export type ReleaseResult =
  | { kind: 'released'; coverage: InspectionCoverage }
  | { kind: 'kept'; reason: ReleaseOutcome }
  // Offline: the server is told later; whether it gives anything back is not known yet.
  | { kind: 'queued' }
  // Nothing paid for it (a Demo, or the server never recorded it).
  | { kind: 'none' };

// consentRequired / consentNotRecorded: a web subscription is never opened for payment without the
// inspector's automatic-renewal tick, and never unless the server has kept it (item 39, NY GBL §527-a).
export type PurchaseFailureReason = PurchaseFailureCode | 'offline' | 'notSignedIn' | 'notEligible' | 'consentRequired' | 'consentNotRecorded';

// What a buy button passes along: on the web, whether the automatic-renewal terms were ticked.
export interface PurchaseOptions {
  autoRenewConsent?: boolean;
}

export type PurchaseResult =
  | { kind: 'purchased'; product: BillingProductKey }
  | { kind: 'cancelled' }
  | { kind: 'pending' }
  | { kind: 'failed'; reason: PurchaseFailureReason };

export type RestoreResult = { kind: 'restored'; plan: PlanKind | null; singlePurchases: number } | { kind: 'failed'; reason: PurchaseFailureReason };

export type ConsumeResult =
  | { kind: 'consumed'; coverage: InspectionCoverage; synced: boolean }
  | { kind: 'alreadyConsumed'; coverage: InspectionCoverage }
  | { kind: 'noCredit' };

export type OfferCodeState = 'idle' | 'waiting' | 'applied' | 'notDetected';

export interface BillingState {
  started: boolean;
  userId: string | null;
  storeConfigured: boolean;
  storePlatform: 'ios' | 'web' | 'fake' | 'unavailable';
  serverState: BillingServerState;
  server: BillingStatus | null;
  store: StoreEntitlement | null;
  pending: PendingConsumption[];
  unfunded: string[];
  notices: BillingNotice[];
  lastSyncedAt: number | null;
  busy: BillingBusy | null;
  lastPurchase: PurchaseResult | null;
  offerCode: OfferCodeState;
  // ---- derived for the UI (recomputed on every change; never stored twice) ----
  plan: PlanKind | null;
  activeUntil: number | null;
  willRenew: boolean | null;
  singleCredits: number;
  // Free inspections this account still has (Owner 2026-09-11 「头三次免费」), server-counted.
  trialRemaining: number;
  remainingMicros: number;
  remainingInspections: number;
  canBuyAddOn: boolean;
  internalTester: boolean;
  managementUrl: string | null;
}

function emptyState(): BillingState {
  return {
    started: false,
    userId: null,
    storeConfigured: false,
    storePlatform: 'unavailable',
    serverState: 'unknown',
    server: null,
    store: null,
    pending: [],
    unfunded: [],
    notices: [],
    lastSyncedAt: null,
    busy: null,
    lastPurchase: null,
    offerCode: 'idle',
    plan: null,
    activeUntil: null,
    willRenew: null,
    singleCredits: 0,
    trialRemaining: 0,
    remainingMicros: 0,
    remainingInspections: 0,
    canBuyAddOn: false,
    internalTester: false,
    managementUrl: null
  };
}

export const billingStore = createStore<BillingState>(emptyState());

let clock: () => number = () => Date.now();

export function setBillingClockForTests(now: (() => number) | null): void {
  clock = now ?? (() => Date.now());
}

function online(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine !== false;
}

// ---------- derived state ----------

function serverCoverageOf(state: BillingState, inspectionId: string): InspectionCoverage | null {
  return state.server?.coverage.find((record) => record.inspectionId === inspectionId)?.coverage ?? null;
}

function unsyncedSingles(state: { server: BillingStatus | null; pending: PendingConsumption[] }): number {
  return state.pending.filter((entry) => entry.coverage === 'single' && !entry.rejected && !state.server?.coverage.some((record) => record.inspectionId === entry.inspectionId)).length;
}

function derive(state: BillingState): BillingState {
  const now = clock();
  const server = state.server;
  const store = state.store;
  const serverActive = server?.plan !== null && server?.plan !== undefined && server.activeUntil !== null && server.activeUntil > now;
  const storeActive = store?.plan !== null && store?.plan !== undefined && store.activeUntil !== null && store.activeUntil > now;
  const plan = serverActive ? (server?.plan ?? null) : storeActive ? (store?.plan ?? null) : null;
  const activeUntil = serverActive ? (server?.activeUntil ?? null) : storeActive ? (store?.activeUntil ?? null) : (server?.activeUntil ?? store?.activeUntil ?? null);
  // Purchases the store knows about but the server has not synced yet still count: the receipt is
  // real, the ledger is only behind. Consumptions can only ever REDUCE the number.
  const purchases = Math.max(server?.singlePurchases ?? 0, store?.singlePurchases ?? 0);
  const singleCredits = Math.max(0, purchases - (server?.singleConsumed ?? 0) - unsyncedSingles(state));
  const remainingMicros = server?.remainingMicros ?? 0;
  return {
    ...state,
    plan,
    activeUntil,
    willRenew: serverActive ? (server?.willRenew ?? null) : (store?.willRenew ?? server?.willRenew ?? null),
    singleCredits,
    trialRemaining: server?.trialRemaining ?? 0,
    remainingMicros,
    // 「还剩几单」 (Owner 2026-10-19): the server's count of pool + pack + gift 单.
    remainingInspections: server?.remainingUnits ?? 0,
    canBuyAddOn: plan !== null && state.storeConfigured,
    internalTester: server?.internalTester === true,
    managementUrl: server?.managementUrl ?? store?.managementUrl ?? null
  };
}

function update(updater: (prev: BillingState) => BillingState): void {
  billingStore.set((prev) => derive(updater(prev)));
}

// WHOSE ANSWER IS THIS (account isolation, Owner device test 2026-09-21). A round trip that started
// under one account may come back after that inspector signed out and another one signed in. Its
// plan, its allowance and its coverage are not the new account's: the answer is dropped — it is
// never shown to, cached for or counted against whoever is on the device now.
function billingAccountGuard(): () => boolean {
  const owner = billingStore.get().userId;
  return () => billingStore.get().userId === owner;
}

// ---------- offline cache (yanwushi-v2 namespace only) ----------

interface CachedAccount {
  email: string | null;
  status: unknown;
  pending: PendingConsumption[];
  unfunded: string[];
  notices?: BillingNotice[];
  syncedAt: number | null;
}

function isNotice(value: unknown): value is BillingNotice {
  const entry = value as Partial<BillingNotice> | null;
  return typeof entry === 'object' && entry !== null && (entry.kind === 'release' || entry.kind === 'close' || entry.kind === 'hold') && typeof entry.inspectionId === 'string' && entry.inspectionId.length > 0;
}

function readCache(): Record<string, CachedAccount> {
  try {
    assertV2Namespace(V2_BILLING_CACHE_KEY);
    const raw = window.localStorage.getItem(V2_BILLING_CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as { v?: number; accounts?: Record<string, CachedAccount> };
    return parsed && typeof parsed === 'object' && parsed.accounts && typeof parsed.accounts === 'object' ? parsed.accounts : {};
  } catch {
    return {};
  }
}

function writeCache(accounts: Record<string, CachedAccount>): void {
  try {
    // Keep the file small: the five most recently synced accounts is far more than a device needs.
    const entries = Object.entries(accounts)
      .sort((a, b) => (b[1].syncedAt ?? 0) - (a[1].syncedAt ?? 0))
      .slice(0, 5);
    window.localStorage.setItem(V2_BILLING_CACHE_KEY, JSON.stringify({ v: 1, accounts: Object.fromEntries(entries) }));
  } catch {
    // storage refusal must never block field work
  }
}

function persist(state: BillingState): void {
  if (!state.userId) return;
  const accounts = readCache();
  accounts[state.userId] = {
    email: sessionStore.get().email,
    status: state.server,
    pending: state.pending,
    unfunded: state.unfunded,
    notices: state.notices,
    syncedAt: state.lastSyncedAt
  };
  writeCache(accounts);
}

function restoreFromCache(userId: string | null, email: string | null): { server: BillingStatus | null; pending: PendingConsumption[]; unfunded: string[]; notices: BillingNotice[]; syncedAt: number | null; userId: string | null } {
  const accounts = readCache();
  const key = userId ?? Object.keys(accounts).find((id) => accounts[id].email !== null && accounts[id].email === email) ?? null;
  if (!key || !accounts[key]) return { server: null, pending: [], unfunded: [], notices: [], syncedAt: null, userId };
  const entry = accounts[key];
  return {
    server: checkBillingStatus(entry.status),
    pending: Array.isArray(entry.pending) ? entry.pending.filter((item) => typeof item?.inspectionId === 'string') : [],
    unfunded: Array.isArray(entry.unfunded) ? entry.unfunded.filter((item) => typeof item === 'string') : [],
    notices: Array.isArray(entry.notices) ? entry.notices.filter(isNotice) : [],
    syncedAt: typeof entry.syncedAt === 'number' ? entry.syncedAt : null,
    // Offline continuation: the session has no live user id, but the remembered email names the
    // account whose cache this is.
    userId: userId ?? key
  };
}

// ---------- lifecycle ----------

let starting: Promise<void> | null = null;
let stopEntitlementListener: (() => void) | null = null;
let listenersBound = false;

function bindListeners(): void {
  if (listenersBound) return;
  listenersBound = true;
  sessionStore.subscribe(() => {
    const phase = sessionStore.get().phase;
    if (phase === 'signedOut') {
      // The RevenueCat SDK is deliberately NOT logged out: logOut() switches it to an anonymous id
      // that can itself hold and restore purchases (memory yanwushi-auth-gate §2). The in-memory
      // state is cleared; the per-account cache stays for the next sign-in.
      stopEntitlementListener?.();
      stopEntitlementListener = null;
      starting = null;
      // A round trip of the account that just left is not the next account's refresh: whoever signs
      // in next asks for their own numbers instead of waiting on (and inheriting) this one.
      refreshing = null;
      billingStore.set(() => emptyState());
    }
  });
  if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
    window.addEventListener('online', () => {
      void refreshBilling();
    });
  }
  // Android acceptance 2026-10-21 (issue 19): 「另一台设备上还有 N 单没做完」 was only asked for at a cold
  // start, so an inspection begun on the web while the app stayed open never showed. Coming back to the
  // app asks again — at most once a minute.
  if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') return;
      const state = billingStore.get();
      if (!state.started || state.userId === null) return;
      if (state.lastSyncedAt !== null && clock() - state.lastSyncedAt < 60_000) return;
      void refreshBilling();
    });
  }
}

export async function ensureBillingStarted(): Promise<void> {
  if (billingStore.get().started) return;
  if (starting) return starting;
  starting = (async () => {
    bindListeners();
    const adapter = getPurchasesAdapter();
    const identity = await currentBillingIdentity();
    const email = sessionStore.get().email;
    const cached = restoreFromCache(identity?.userId ?? null, email);
    update((prev) => ({
      ...prev,
      started: true,
      userId: cached.userId,
      storeConfigured: adapter.configured(),
      storePlatform: adapter.platform,
      server: cached.server,
      pending: cached.pending,
      unfunded: cached.unfunded,
      notices: cached.notices,
      lastSyncedAt: cached.syncedAt
    }));
    if (identity && adapter.configured()) {
      const outcome = await adapter.identify(identity.userId);
      if (outcome.ok) {
        update((prev) => ({ ...prev, store: outcome.entitlement }));
        stopEntitlementListener?.();
        stopEntitlementListener = adapter.onEntitlementChange((entitlement) => {
          update((prev) => ({ ...prev, store: entitlement }));
          void refreshBilling();
        });
      }
    }
    // Always ask, even offline: the transport answers "offline" without a round trip, and the state
    // then says so instead of pretending the cached numbers were just confirmed.
    await refreshBilling();
  })();
  try {
    await starting;
  } finally {
    starting = null;
  }
}

function serverStateFor(code: BillingFailureCode): BillingServerState {
  if (code === 'offline' || code === 'network' || code === 'timeout') return 'offline';
  if (code === 'notConfigured') return 'notConfigured';
  if (code === 'unauthenticated') return 'unauthenticated';
  return 'unavailable';
}

function applyStatus(status: BillingStatus): void {
  update((prev) => {
    const covered = new Set(status.coverage.map((record) => record.inspectionId));
    const unfunded = new Set(prev.unfunded);
    for (const record of status.coverage) {
      if (record.coverage === 'unfunded') unfunded.add(record.inspectionId);
      else unfunded.delete(record.inspectionId);
    }
    return {
      ...prev,
      server: status,
      serverState: 'ok',
      lastSyncedAt: clock(),
      // Anything the server now knows about is no longer pending.
      pending: prev.pending.filter((entry) => !covered.has(entry.inspectionId)),
      unfunded: [...unfunded]
    };
  });
  persist(billingStore.get());
}

let refreshing: Promise<void> | null = null;

// Pulls the server's view and flushes whatever this device consumed while it was offline. Two
// callers at once share ONE round trip — and the second one waits for it rather than reading a
// half-updated state.
export async function refreshBilling(): Promise<void> {
  await ensureBillingStarted();
  if (refreshing) return refreshing;
  const sameAccount = billingAccountGuard();
  const mine = (async () => {
    update((prev) => ({ ...prev, busy: 'refresh' }));
    try {
      await flushPendingConsumptions(sameAccount);
      if (!sameAccount()) return;
      await flushNotices(sameAccount);
      if (!sameAccount()) return;
      const token = await currentAccessToken();
      if (!sameAccount()) return;
      const outcome = await getBillingTransport().status(token);
      if (!sameAccount()) return;
      if (outcome.kind === 'ok') applyStatus(outcome.response.status);
      else update((prev) => ({ ...prev, serverState: serverStateFor(outcome.code) }));
    } finally {
      if (sameAccount()) update((prev) => ({ ...prev, busy: null }));
    }
  })();
  refreshing = mine;
  try {
    await mine;
  } finally {
    if (refreshing === mine) refreshing = null;
  }
}

async function flushPendingConsumptions(sameAccount: () => boolean): Promise<void> {
  const state = billingStore.get();
  const queue = state.pending.filter((entry) => !entry.rejected);
  if (queue.length === 0 || !online()) return;
  const token = await currentAccessToken();
  for (const entry of queue) {
    // One account's consumptions are never reported under another account's sign-in.
    if (!sameAccount()) return;
    const outcome = await getBillingTransport().consume(token, { inspectionId: entry.inspectionId, clientCreatedAt: entry.at, offline: true, device: thisDevice() });
    if (!sameAccount()) return;
    if (outcome.kind !== 'ok') {
      update((prev) => ({ ...prev, serverState: serverStateFor(outcome.code) }));
      return;
    }
    applyStatus(outcome.response.status);
    if (outcome.response.coverage === 'unfunded') {
      update((prev) => ({
        ...prev,
        pending: prev.pending.map((item) => (item.inspectionId === entry.inspectionId ? { ...item, rejected: true } : item)),
        unfunded: prev.unfunded.includes(entry.inspectionId) ? prev.unfunded : [...prev.unfunded, entry.inspectionId]
      }));
      persist(billingStore.get());
    }
  }
}

// ---------- the API the screens use ----------

// May this inspector create an inspection of this kind right now? A Demo is always free (X-4); a
// formal inspection needs an active plan, an unused single purchase or a free inspection (D-5).
export async function ensureInspectionAllowed(kind: InspectionKind): Promise<InspectionPermission> {
  if (kind === 'demo') return 'allowed';
  await ensureBillingStarted();
  // A build with no store (the RevenueCat key is not in it) cannot sell anything, so it must not
  // demand a purchase either — the paywall would be a dead end in the field. Default X-8 of
  // 2026-09-11, open for the Owner to overrule; a shipping build always carries the key.
  // NOT on a host with no purchase lane at all (a web build without its purchase link, 2026-10-20): there
  // the server's answer decides — a plan or purchase from the apps, a free report — never a free pass.
  if (!billingStore.get().storeConfigured && billingStore.get().storePlatform !== 'unavailable') return 'allowed';
  const before = billingStore.get();
  // A stale answer is worth one round trip before charging someone: refresh when the device is
  // online and the numbers are older than a minute.
  if (online() && (before.serverState !== 'ok' || before.lastSyncedAt === null || clock() - before.lastSyncedAt > 60_000)) await refreshBilling();
  const state = billingStore.get();
  const coverage = coverageAvailable({
    plan: state.plan,
    internalTester: state.internalTester,
    singleCredits: state.singleCredits,
    trialRemaining: state.server?.trialRemaining ?? 0
  });
  if (coverage !== null) return 'allowed';
  if (!online()) return 'offlineNoCredit';
  // The free inspections and the purchases are counted on the server (X-7). If it could not be
  // reached, we do not know what this account has — and an unknown is never a reason to sell.
  if (state.serverState !== 'ok') return 'serverUnavailable';
  return 'needsPurchase';
}

// Records that this inspection used one purchase / one plan slot. Idempotent per inspection: the
// caller may retry after a lost answer, and a second call never charges twice.
export async function consumeInspectionCredit(inspectionId: string): Promise<ConsumeResult> {
  await ensureBillingStarted();
  const state = billingStore.get();
  const known = serverCoverageOf(state, inspectionId) ?? state.pending.find((entry) => entry.inspectionId === inspectionId)?.coverage ?? null;
  if (known) return { kind: 'alreadyConsumed', coverage: known };
  const coverage = coverageAvailable({
    plan: state.plan,
    internalTester: state.internalTester,
    singleCredits: state.singleCredits,
    trialRemaining: state.server?.trialRemaining ?? 0
  });
  if (coverage === null) return { kind: 'noCredit' };
  const at = clock();
  update((prev) => ({ ...prev, pending: [...prev.pending, { inspectionId, coverage, at }] }));
  persist(billingStore.get());
  if (!online()) return { kind: 'consumed', coverage, synced: false };
  const sameAccount = billingAccountGuard();
  const token = await currentAccessToken();
  // Signed out meanwhile: the consumption stays in this account's own pending list (written above)
  // and is reported at its next sign-in — never under the next inspector's session.
  if (!sameAccount()) return { kind: 'consumed', coverage, synced: false };
  const outcome = await getBillingTransport().consume(token, { inspectionId, clientCreatedAt: at, offline: false, device: thisDevice() });
  if (!sameAccount()) return { kind: 'consumed', coverage, synced: false };
  if (outcome.kind !== 'ok') {
    update((prev) => ({ ...prev, serverState: serverStateFor(outcome.code) }));
    return { kind: 'consumed', coverage, synced: false };
  }
  applyStatus(outcome.response.status);
  const recorded = outcome.response.coverage ?? coverage;
  if (recorded === 'unfunded') {
    update((prev) => ({ ...prev, unfunded: prev.unfunded.includes(inspectionId) ? prev.unfunded : [...prev.unfunded, inspectionId] }));
    persist(billingStore.get());
  }
  return { kind: 'consumed', coverage: recorded, synced: true };
}

// ---------- an unfinished inspection lives on ONE device (billingPolicy.ts V-1 … V-3, Owner 2026-10-20) ----------

function queueNotice(kind: BillingNotice['kind'], inspectionId: string): void {
  update((prev) => ({
    ...prev,
    notices: [...prev.notices.filter((notice) => !(notice.kind === kind && notice.inspectionId === inspectionId)), { kind, inspectionId, at: clock() }]
  }));
  persist(billingStore.get());
}

function dropNotice(kind: BillingNotice['kind'], inspectionId: string): void {
  update((prev) => ({ ...prev, notices: prev.notices.filter((notice) => !(notice.kind === kind && notice.inspectionId === inspectionId)) }));
  persist(billingStore.get());
}

// One notice to the server. ok = the server took it (whatever it decided); false = try again later.
async function sendNotice(notice: BillingNotice, token: string | null, sameAccount: () => boolean): Promise<{ sent: boolean; release?: ReleaseResult }> {
  const transport = getBillingTransport();
  const outcome =
    notice.kind === 'release'
      ? await transport.release(token, { inspectionId: notice.inspectionId, device: thisDevice() })
      : notice.kind === 'close'
        ? await transport.close(token, { inspectionId: notice.inspectionId })
        : await transport.hold(token, { inspectionId: notice.inspectionId, device: thisDevice() });
  if (!sameAccount()) return { sent: false };
  if (outcome.kind !== 'ok') {
    update((prev) => ({ ...prev, serverState: serverStateFor(outcome.code) }));
    return { sent: false };
  }
  applyStatus(outcome.response.status);
  dropNotice(notice.kind, notice.inspectionId);
  if (notice.kind === 'hold' && outcome.response.hold?.outcome === 'notFound') {
    // The server has no record of what paid for it (it was given back on the device it came from, or that
    // device never got online to record it): it is recorded now, the way an inspection created offline is.
    const state = billingStore.get();
    const guess = coverageAvailable({ plan: state.plan, internalTester: state.internalTester, singleCredits: state.singleCredits, trialRemaining: state.trialRemaining }) ?? 'unfunded';
    update((prev) => (prev.pending.some((entry) => entry.inspectionId === notice.inspectionId) ? prev : { ...prev, pending: [...prev.pending, { inspectionId: notice.inspectionId, coverage: guess, at: clock() }] }));
    persist(billingStore.get());
  }
  if (notice.kind === 'release') {
    const answer = outcome.response.release;
    const release: ReleaseResult = !answer || answer.outcome === 'notFound' ? { kind: 'none' } : answer.outcome === 'released' && answer.coverage ? { kind: 'released', coverage: answer.coverage } : { kind: 'kept', reason: answer.outcome };
    return { sent: true, release };
  }
  return { sent: true };
}

async function flushNotices(sameAccount: () => boolean): Promise<void> {
  const queue = billingStore.get().notices;
  if (queue.length === 0 || !online()) return;
  const token = await currentAccessToken();
  for (const notice of queue) {
    if (!sameAccount()) return;
    const sent = await sendNotice(notice, token, sameAccount);
    if (!sent.sent) return;
  }
  // A hold that found no record queued a consumption above.
  await flushPendingConsumptions(sameAccount);
}

// V-1: an unfinished formal inspection was deleted on this device. What paid for it comes back when the
// server agrees (no AI used, still on this device, within the limit). A consumption this device never got
// to report is simply dropped: the server never counted it.
export async function releaseInspectionCredit(inspectionId: string): Promise<ReleaseResult> {
  await ensureBillingStarted();
  const state = billingStore.get();
  const pending = state.pending.find((entry) => entry.inspectionId === inspectionId) ?? null;
  const known = serverCoverageOf(state, inspectionId);
  if (!pending && known === null) return { kind: 'none' };
  // Only this device knew about the consumption: dropping it is the give-back. The server is told all the
  // same — its answer to the consumption may have been lost on the way back.
  const unreported: ReleaseResult | null =
    pending && !pending.rejected && known === null && (pending.coverage === 'single' || pending.coverage === 'trial') ? { kind: 'released', coverage: pending.coverage } : null;
  update((prev) => ({ ...prev, pending: prev.pending.filter((entry) => entry.inspectionId !== inspectionId) }));
  queueNotice('release', inspectionId);
  if (!online()) return unreported ?? { kind: 'queued' };
  const sameAccount = billingAccountGuard();
  const token = await currentAccessToken();
  const notice = billingStore.get().notices.find((entry) => entry.kind === 'release' && entry.inspectionId === inspectionId);
  if (!sameAccount() || !notice) return unreported ?? { kind: 'queued' };
  const sent = await sendNotice(notice, token, sameAccount);
  if (!sent.sent || !sent.release) return unreported ?? { kind: 'queued' };
  return sent.release.kind === 'none' && unreported ? unreported : sent.release;
}

// V-2: the final report of this inspection was generated here — it is no longer open anywhere.
export async function closeInspectionCredit(inspectionId: string): Promise<void> {
  await ensureBillingStarted();
  queueNotice('close', inspectionId);
  await flushNotices(billingAccountGuard());
}

// V-3: a file carried this inspection to this device, which holds it from now on.
export async function holdImportedInspection(inspectionId: string): Promise<void> {
  await ensureBillingStarted();
  queueNotice('hold', inspectionId);
  await flushNotices(billingAccountGuard());
}

// What paid for this inspection as far as this device knows: the server's record, or a consumption still
// waiting to be reported. null = nothing (a Demo, or not known here).
export function coverageOfInspection(inspectionId: string, state: BillingState = billingStore.get()): InspectionCoverage | null {
  return serverCoverageOf(state, inspectionId) ?? state.pending.find((entry) => entry.inspectionId === inspectionId && !entry.rejected)?.coverage ?? null;
}

// V-2: what the server says about where this inspection is open, or null when it does not know.
export function serverRecordOf(inspectionId: string): CoverageRecord | null {
  return billingStore.get().server?.coverage.find((record) => record.inspectionId === inspectionId) ?? null;
}

// V-2: inspections this account has open on another device and that are not on this one at all.
export function inspectionsOpenElsewhere(state: BillingState, localIds: ReadonlySet<string>): CoverageRecord[] {
  if (!state.server) return [];
  return openElsewhere(state.server.coverage, deviceId()).filter((record) => !localIds.has(record.inspectionId));
}

async function runPurchase(product: BillingProductKey, options: PurchaseOptions = {}): Promise<PurchaseResult> {
  await ensureBillingStarted();
  const adapter = getPurchasesAdapter();
  const state = billingStore.get();
  if (!adapter.configured()) return finishPurchase({ kind: 'failed', reason: 'notConfigured' });
  if (!online()) return finishPurchase({ kind: 'failed', reason: 'offline' });
  // D-3: the add-on pack exists only for an active monthly / annual subscriber.
  if (product === 'addOn' && state.plan === null) return finishPurchase({ kind: 'failed', reason: 'notEligible' });
  const subscription = product === 'monthly' || product === 'annual';
  // Item 39 (NY GBL §527-a): on the web the automatic-renewal terms are ticked before the payment page opens.
  if (adapter.platform === 'web' && subscription && options.autoRenewConsent !== true) return finishPurchase({ kind: 'failed', reason: 'consentRequired' });
  const identity = await currentBillingIdentity();
  if (!identity) return finishPurchase({ kind: 'failed', reason: 'notSignedIn' });
  update((prev) => ({ ...prev, busy: product, lastPurchase: null }));
  try {
    const identified = await adapter.identify(identity.userId);
    if (!identified.ok) return finishPurchase({ kind: 'failed', reason: identified.code });
    update((prev) => ({ ...prev, store: identified.entitlement }));
    if (adapter.platform === 'web' && subscription) {
      // The server keeps the tick (with its own price and the terms version) BEFORE the page opens; if it
      // cannot, nothing is opened.
      const kept = await getWebBillingTransport().consent(await currentAccessToken(), product);
      if (!kept.ok) return finishPurchase({ kind: 'failed', reason: kept.code === 'offline' ? 'offline' : kept.code === 'unauthenticated' ? 'notSignedIn' : 'consentNotRecorded' });
    }
    if (adapter.platform === 'web') rememberWebCheckout(product, identity.userId);
    const outcome = await adapter.purchase(product);
    if (outcome.kind === 'cancelled') return finishPurchase({ kind: 'cancelled' });
    if (outcome.kind === 'pending') return finishPurchase({ kind: 'pending' });
    if (outcome.kind === 'failed') return finishPurchase({ kind: 'failed', reason: outcome.code });
    update((prev) => ({ ...prev, store: outcome.entitlement }));
    return finishPurchase({ kind: 'purchased', product });
  } finally {
    update((prev) => ({ ...prev, busy: null }));
  }
}

function finishPurchase(result: PurchaseResult): PurchaseResult {
  update((prev) => ({ ...prev, lastPurchase: result, busy: null }));
  // The server reads the store directly, so a successful purchase is visible to it at once.
  if (result.kind === 'purchased' && online()) void refreshBilling();
  return result;
}

export const purchaseSingle = (options?: PurchaseOptions): Promise<PurchaseResult> => runPurchase('single', options);
export const purchaseMonthly = (options?: PurchaseOptions): Promise<PurchaseResult> => runPurchase('monthly', options);
export const purchaseAnnual = (options?: PurchaseOptions): Promise<PurchaseResult> => runPurchase('annual', options);
export const purchaseAddOn = (options?: PurchaseOptions): Promise<PurchaseResult> => runPurchase('addOn', options);

// "Restore Purchases" — required by Apple on every screen that sells something.
export async function restorePurchases(): Promise<RestoreResult> {
  await ensureBillingStarted();
  const adapter = getPurchasesAdapter();
  if (!adapter.configured()) return { kind: 'failed', reason: 'notConfigured' };
  if (!online()) return { kind: 'failed', reason: 'offline' };
  const identity = await currentBillingIdentity();
  if (!identity) return { kind: 'failed', reason: 'notSignedIn' };
  update((prev) => ({ ...prev, busy: 'restore' }));
  try {
    const identified = await adapter.identify(identity.userId);
    if (!identified.ok) return { kind: 'failed', reason: identified.code };
    const outcome = await adapter.restore();
    if (!outcome.ok) return { kind: 'failed', reason: outcome.code };
    update((prev) => ({ ...prev, store: outcome.entitlement }));
    await refreshBilling();
    return { kind: 'restored', plan: outcome.entitlement.plan, singlePurchases: outcome.entitlement.singlePurchases };
  } finally {
    update((prev) => ({ ...prev, busy: null }));
  }
}

// X-5: the promo-code entry opens APPLE's redemption sheet. The app never validates a code itself
// and never unlocks paid content with a code of its own (App Store Review 3.1.1). What comes back
// is a purchase like any other, which the store listener and the next refresh pick up.
// What the account looked like when Apple's sheet was opened. "Did anything arrive?" is answered by
// comparing against THIS, never by trusting a code the app never sees.
let offerCodeSignature: string | null = null;

function accountSignature(state: BillingState): string {
  return [state.plan ?? '', state.activeUntil ?? '', state.server?.singlePurchases ?? 0, state.server?.addOnRemainingUnits ?? 0, state.store?.singlePurchases ?? 0, state.store?.addOnPurchases ?? 0].join(':');
}

export async function redeemOfferCode(): Promise<{ kind: 'presented' } | { kind: 'failed'; reason: PurchaseFailureReason }> {
  await ensureBillingStarted();
  const adapter = getPurchasesAdapter();
  if (!adapter.configured()) return { kind: 'failed', reason: 'notConfigured' };
  if (!online()) return { kind: 'failed', reason: 'offline' };
  const identity = await currentBillingIdentity();
  if (!identity) return { kind: 'failed', reason: 'notSignedIn' };
  update((prev) => ({ ...prev, busy: 'offerCode', offerCode: 'idle' }));
  offerCodeSignature = accountSignature(billingStore.get());
  try {
    const identified = await adapter.identify(identity.userId);
    if (!identified.ok) return { kind: 'failed', reason: identified.code };
    const outcome = await adapter.presentOfferCodeSheet();
    if (!outcome.ok) return { kind: 'failed', reason: outcome.code };
    update((prev) => ({ ...prev, offerCode: 'waiting' }));
    return { kind: 'presented' };
  } finally {
    update((prev) => ({ ...prev, busy: null }));
  }
}

// After Apple's sheet closes: did anything actually arrive? Apple tells the inspector whether the
// code was valid; the app only reports whether the ACCOUNT changed since the sheet was opened, and
// never claims otherwise. A redemption Apple finishes late is caught by checking again.
export async function checkOfferCodeResult(): Promise<OfferCodeState> {
  const adapter = getPurchasesAdapter();
  const entitlement = await adapter.entitlement();
  if (entitlement) update((prev) => ({ ...prev, store: entitlement }));
  await refreshBilling();
  const state: OfferCodeState = offerCodeSignature !== null && offerCodeSignature !== accountSignature(billingStore.get()) ? 'applied' : 'notDetected';
  update((prev) => ({ ...prev, offerCode: state }));
  return state;
}

export function dismissOfferCodeState(): void {
  update((prev) => ({ ...prev, offerCode: 'idle' }));
}

// Apple requires subscription management to live in the system settings page, never in the app.
export function openManageSubscriptions(): boolean {
  const state = billingStore.get();
  return getPurchasesAdapter().openManageSubscriptions(state.managementUrl);
}

// The AI allowance the server last reported, applied from an AI answer so the plan page and the AI
// card agree without a second round trip.
export function applyServerQuota(quota: AiV2Quota): void {
  update((prev) => {
    if (!prev.server) return prev;
    return {
      ...prev,
      server: {
        ...prev.server,
        remainingMicros: quota.remainingMicros,
        remainingInspections: quota.remainingInspections,
        canBuyAddOn: quota.canBuyAddOn
      },
      lastSyncedAt: clock()
    };
  });
  persist(billingStore.get());
}

// Why the AI button should say "allowance used up" before it is even pressed. null = nothing known
// to be in the way; the server still has the final word on every request. Since 2026-10-19 an
// inspection can run AI while (in the server's order) its opened 单 still carry AI, the account has
// pool / pack / gift 单 left, or — for an inspection paid by a single purchase or a free report —
// another purchase or free report could pay its next 单.
export function aiQuotaBlockFor(inspectionId: string | null): 'quotaExhausted' | null {
  const state = billingStore.get();
  if (state.internalTester || state.server === null) return null;
  if (inspectionId && (state.server.inspectionRemainingMicros[inspectionId] ?? 0) > 0) return null;
  if (state.server.remainingUnits > 0) return null;
  const coverage = inspectionId ? serverCoverageOf(state, inspectionId) : null;
  if ((coverage === 'single' || coverage === 'trial') && (state.singleCredits > 0 || state.trialRemaining > 0)) return null;
  return 'quotaExhausted';
}

// Owner decision list 2026-10-19 item 46 (P-6): a redemption code becomes gift 单 on this account. The
// WEB build only calls this — an app never unlocks anything with a code of its own (App Store Review
// 3.1.1) — and the inspector must have ticked that the Demo inspections made with these 单 may be used,
// anonymised, to measure AI accuracy.
export type RedeemResult = { kind: 'done'; outcome: RedeemOutcome; units: number } | { kind: 'failed'; reason: 'offline' | 'notSignedIn' | 'unavailable' };

export async function redeemCodeAction(code: string, consent: boolean): Promise<RedeemResult> {
  if (!consent) return { kind: 'done', outcome: 'invalid', units: 0 };
  await ensureBillingStarted();
  if (!online()) return { kind: 'failed', reason: 'offline' };
  const sameAccount = billingAccountGuard();
  update((prev) => ({ ...prev, busy: 'redeem' }));
  try {
    const token = await currentAccessToken();
    if (!token) return { kind: 'failed', reason: 'notSignedIn' };
    const outcome = await getBillingTransport().redeem(token, { code, consent: true });
    if (!sameAccount()) return { kind: 'failed', reason: 'notSignedIn' };
    if (outcome.kind !== 'ok') {
      update((prev) => ({ ...prev, serverState: serverStateFor(outcome.code) }));
      return { kind: 'failed', reason: outcome.code === 'offline' || outcome.code === 'network' || outcome.code === 'timeout' ? 'offline' : outcome.code === 'unauthenticated' ? 'notSignedIn' : 'unavailable' };
    }
    applyStatus(outcome.response.status);
    return { kind: 'done', outcome: outcome.response.redeem?.outcome ?? 'invalid', units: outcome.response.redeem?.units ?? 0 };
  } finally {
    if (sameAccount()) update((prev) => ({ ...prev, busy: null }));
  }
}

// The web version shows the web prices (item 39); the store apps the store prices.
export function priceLabel(product: BillingProductKey): string {
  return getPurchasesAdapter().platform === 'web' ? BILLING_WEB_PRICE_LABELS[product] : BILLING_PRICE_LABELS[product];
}

// The purchase the browser just came back from (src/state/webPurchaseReturn.ts): shown as "being confirmed"
// while the server catches up with RevenueCat.
export function noteWebPurchaseReturn(): void {
  update((prev) => ({ ...prev, lastPurchase: { kind: 'pending' } }));
}

export function noteWebPurchaseConfirmed(product: BillingProductKey): void {
  update((prev) => ({ ...prev, lastPurchase: { kind: 'purchased', product } }));
}

export function resetBillingForTests(): void {
  stopEntitlementListener?.();
  stopEntitlementListener = null;
  starting = null;
  refreshing = null;
  offerCodeSignature = null;
  billingStore.set(() => emptyState());
  try {
    window.localStorage.removeItem(V2_BILLING_CACHE_KEY);
  } catch {
    // nothing to clear
  }
}
